import { message, notification } from "antd";
import { authController } from "../../AppController";
import { APIRoutes } from "../../config";

/**
 * Shared opener for Neo project documents (workflow run outputs, Files-panel
 * documents, conversation document chips).
 *
 * The files are S3 objects behind a bearer-token API, so a plain link can't
 * reach them directly. Instead of the old approach — fetching all the bytes
 * with the token and juggling blob URLs (which broke "Save" in the PDF viewer
 * once the object URL was revoked, and tripped Chrome's one-automatic-download
 * limit when the fetch outlived the click gesture) — we ask the backend for a
 * short-lived presigned S3 URL and hand that real https URL to the browser.
 */

// The presign endpoint bypasses the Amplify API client, so build the raw URL
// the same way the rest of the Neo page's binary downloads do.
const getApiBaseUrl = (): string =>
  import.meta.env.VITE_API_GATEWAY_URL_V2 ||
  import.meta.env.VITE_API_GATEWAY_URL ||
  "";

export interface ProjectDocumentRef {
  projectId?: number | null;
  fileId: number;
  /** Display name for the fallback notification link. */
  name?: string;
  /** "auto" (default): PDFs open inline in a new tab, everything else
   *  downloads. "download": always download — for explicit Download buttons
   *  (e.g. the conversation document chips), even on PDFs. */
  mode?: "auto" | "download";
}

interface MintedUrl {
  url: string;
  /** Whether the URL's baked-in disposition renders inline (new tab). */
  inline: boolean;
  mintedAt: number;
}

class ProjectDocumentHttpError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`HTTP ${status}`);
    this.name = "ProjectDocumentHttpError";
    this.status = status;
  }
}

export const isProjectDocumentNotFoundError = (error: unknown): boolean =>
  error instanceof ProjectDocumentHttpError && error.status === 404;

/** A presigned URL is only trusted for this long after minting. The signature
 *  itself asks for the SigV4 maximum (7 days), but the URL actually dies when
 *  the backend's temporary signing credentials expire, which can be well
 *  under an hour — and S3 has no CORS config, so the client can't probe.
 *  Ten minutes is safely inside any credential lifetime. */
const FRESH_URL_WINDOW_MS = 10 * 60 * 1000;

/** True while the browser still honours the user's click. ``window.open`` and
 *  automatic downloads are gated on "transient user activation", which
 *  expires a few seconds after the click. Minting is a fast JSON round-trip,
 *  so this is almost always still true. Browsers without the API report
 *  ``undefined`` → assume the gesture is still live. */
const hasTransientActivation = (): boolean =>
  navigator.userActivation?.isActive ?? true;

/** Ask the backend to mint a presigned S3 URL. Throws on failure so callers
 *  keep their own contextual error handling. */
const mintDownloadUrl = async (ref: ProjectDocumentRef): Promise<MintedUrl> => {
  const route =
    ref.projectId != null
      ? APIRoutes.apiNeoProjectDocumentDownloadUrl.replace(
          "{project_id}",
          String(ref.projectId)
        )
      : APIRoutes.apiNeoStandaloneArtifactDownloadUrl;
  const endpoint =
    getApiBaseUrl() +
    route.replace("{file_id}", String(ref.fileId)) +
    (ref.projectId != null && ref.mode === "download"
      ? "?disposition=attachment"
      : "");
  const token = await authController.getAuthenticationToken();
  const res = await fetch(endpoint, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new ProjectDocumentHttpError(res.status);
  const body = (await res.json()) as { url: string; content_type: string };
  return {
    url: body.url,
    inline: ref.mode !== "download" && body.content_type === "application/pdf",
    mintedAt: Date.now(),
  };
};

/** Hand the presigned URL to the browser: inline docs open in a new tab,
 *  attachments download in place (the Content-Disposition baked into the URL
 *  carries the real filename — an ``<a download>`` attribute would be ignored
 *  cross-origin). Returns false when the browser blocked the new tab (only
 *  detectable for the inline path). */
const deliver = (minted: MintedUrl): boolean => {
  if (minted.inline) {
    // Not ``window.open(url, "_blank", "noopener")``: with the noopener
    // feature the call returns null even on success, which would make popup
    // blocking undetectable. Open normally, then sever the opener by hand.
    const win = window.open(minted.url, "_blank");
    if (!win) return false;
    win.opener = null;
    return true;
  }
  const a = document.createElement("a");
  a.href = minted.url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
};

/** Last-resort path — NOT the happy path. Shown only when the click's
 *  activation somehow expired during the mint call (severely degraded API) or
 *  a popup blocker ate the tab: a notification whose link the user clicks
 *  themselves, which is a fresh gesture the browser always honours. If the
 *  notification sat long enough for its URL to go stale, the click re-mints
 *  transparently instead of trusting the old URL. */
const notifyFileReady = (ref: ProjectDocumentRef, minted: MintedUrl): void => {
  const key = `neo-doc-${ref.projectId}-${ref.fileId}-${Date.now()}`;
  const displayName = ref.name || "document";
  notification.open({
    key,
    message: "Your file is ready",
    description: (
      // target="_blank" even for attachments: a worst-case dead URL then
      // shows S3's error in a throwaway tab instead of navigating the SPA.
      <a
        href={minted.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          notification.destroy(key);
          if (Date.now() - minted.mintedAt <= FRESH_URL_WINDOW_MS) return;
          // Stale URL: re-mint under this click's fresh gesture instead.
          event.preventDefault();
          openProjectDocument(ref).catch((e) => {
            message.error(
              e instanceof Error
                ? `Could not open the file: ${e.message}`
                : "Could not open the file."
            );
          });
        }}
      >
        {minted.inline ? "Open" : "Download"} {displayName}
      </a>
    ),
    duration: 0,
  });
};

/**
 * Mint a presigned URL for the document and open/download it.
 *
 * Happy path: one fast JSON call, then the browser navigates straight to S3 —
 * no notification, no extra click. The "Your file is ready" notification only
 * appears when the browser would silently suppress an automatic open.
 *
 * Throws when the URL can't be minted (auth / network / 404) so each call
 * site keeps its own error message and telemetry.
 */
export const openProjectDocument = async (
  ref: ProjectDocumentRef
): Promise<void> => {
  const minted = await mintDownloadUrl(ref);
  if (hasTransientActivation() && deliver(minted)) return;
  notifyFileReady(ref, minted);
};
